import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Grow, Button, IconButton, CircularProgress } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import BackgroundLetterAvatarsLogo from "../../components/AvatarLogo";
import "../../components/Modal/modal.scss";

const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

const MAX_SOURCE_BYTES = 8 * 1024 * 1024; // what we accept off disk
const OUTPUT_SIZE = 256; // square edge we store
const OUTPUT_QUALITY = 0.85;

/**
 * Centre-crops to a square and downscales before encoding, so a 6MB phone
 * photo is stored as ~30KB of base64 rather than 8MB of it. Without this the
 * avatar would dominate every user record and every profile response.
 */
const toSquareDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file is not a readable image."));
      img.onload = () => {
        const edge = Math.min(img.width, img.height);
        const sx = (img.width - edge) / 2;
        const sy = (img.height - edge) / 2;

        const canvas = document.createElement("canvas");
        canvas.width = OUTPUT_SIZE;
        canvas.height = OUTPUT_SIZE;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        // Transparent PNGs would go black over JPEG; paint a white ground first.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
        ctx.drawImage(img, sx, sy, edge, edge, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

        resolve(canvas.toDataURL("image/jpeg", OUTPUT_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const ProfilePhotoDialog = ({ open, onClose, name, currentImage, onSave }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPreview(null);
      setError("");
      setSaving(false);
      setDragging(false);
    }
  }, [open]);

  const accept = useCallback(async (file) => {
    if (!file) return;
    setError("");

    if (!file.type.startsWith("image/")) {
      setError("Pick an image file — JPG, PNG, GIF or WebP.");
      return;
    }
    if (file.size > MAX_SOURCE_BYTES) {
      setError("That image is over 8MB. Try a smaller one.");
      return;
    }
    try {
      setPreview(await toSquareDataUrl(file));
    } catch (err) {
      setError(err.message || "That image could not be processed.");
    }
  }, []);

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    accept(event.dataTransfer.files?.[0]);
  };

  const commit = async (value) => {
    setSaving(true);
    setError("");
    try {
      await onSave(value);
      onClose();
    } catch (err) {
      // Surface what actually went wrong — a silently-dropped field reads very
      // differently from a network failure, and only one of them is retryable.
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Could not save your photo. Please try again."
      );
      setSaving(false);
    }
  };

  const shown = preview || currentImage || null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-modal" }}
      slotProps={{ backdrop: { className: "pj-modal-backdrop" } }}
    >
      <div className="pj-modal-body">
        <IconButton className="pj-modal-close" onClick={onClose} disabled={saving} aria-label="Close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <h3 className="pj-modal-title">Profile photo</h3>
        <p className="pj-modal-sub">A square picture works best. Stored at 256&times;256.</p>

        <div className="pj-photo-preview">
          <BackgroundLetterAvatarsLogo
            LogoTitle={name || ""}
            isCustomised
            RequiredFontSize="46px"
            profileImage={shown}
          />
          {preview ? <span className="pj-photo-badge">New</span> : null}
        </div>

        <button
          type="button"
          className={`pj-drop ${dragging ? "is-dragging" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          disabled={saving}
        >
          <CloudUploadRoundedIcon />
          <span className="pj-drop-main">Click to choose, or drop an image</span>
          <span className="pj-drop-sub">JPG, PNG, GIF or WebP &middot; up to 8MB</span>
        </button>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            accept(e.target.files?.[0]);
            // Let the same file be re-picked after a cancel.
            e.target.value = "";
          }}
        />

        {error ? <p className="pj-modal-error">{error}</p> : null}

        <div className="pj-modal-actions pj-modal-actions--split">
          {currentImage ? (
            <Button
              className="pj-btn pj-btn--ghost"
              onClick={() => commit("")}
              disabled={saving}
              startIcon={<DeleteOutlineRoundedIcon />}
            >
              Remove
            </Button>
          ) : (
            <span />
          )}

          <div className="pj-modal-actions-right">
            <Button className="pj-btn pj-btn--quiet" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              className="pj-btn pj-btn--primary"
              onClick={() => commit(preview)}
              disabled={!preview || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraRoundedIcon />}
            >
              {saving ? "Saving..." : "Save photo"}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ProfilePhotoDialog;
